import type { AppUser } from '@/lib/auth';
import { requireAdminOrManager } from '@/lib/auth/guards';
import { isServiceError, normalizeError } from '@/lib/services/errors';
import {
  addAvailabilityAlertSubscriber,
  countAvailabilityAlertOutbox,
  deactivateAvailabilityAlertSubscriber,
  listAvailabilityAlertSubscribers,
  listUnitAvailabilityAlertEvents,
  reactivateAvailabilityAlertSubscriber,
  subscribeToAvailabilityAlerts,
  type AvailabilityAlertSubscriberRow,
  type UnitAvailabilityAlertEventRow,
} from '@/lib/supabase/availabilityAlerts';

export async function subscribeToAvailabilityAlertsPublic(args: {
  email: string;
}): Promise<AvailabilityAlertSubscriberRow> {
  try {
    return await subscribeToAvailabilityAlerts(args.email);
  } catch (error) {
    handleAvailabilityAlertError(error);
  }
}

export async function listAvailabilityAlertSubscribersForUser(args: {
  user: AppUser | null;
  signal?: AbortSignal;
}): Promise<AvailabilityAlertSubscriberRow[]> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  try {
    return await listAvailabilityAlertSubscribers({ signal: args.signal });
  } catch (error) {
    handleAvailabilityAlertError(error);
  }
}

export async function addAvailabilityAlertSubscriberForUser(args: {
  user: AppUser | null;
  email: string;
}): Promise<AvailabilityAlertSubscriberRow> {
  const actor = requireAdminOrManager(args.user);

  try {
    return await addAvailabilityAlertSubscriber({ email: args.email, actorId: actor.id });
  } catch (error) {
    handleAvailabilityAlertError(error);
  }
}

export async function deactivateAvailabilityAlertSubscriberForUser(args: {
  user: AppUser | null;
  subscriberId: string;
}): Promise<AvailabilityAlertSubscriberRow> {
  const actor = requireAdminOrManager(args.user);

  try {
    return await deactivateAvailabilityAlertSubscriber({
      subscriberId: args.subscriberId,
      actorId: actor.id,
    });
  } catch (error) {
    handleAvailabilityAlertError(error);
  }
}

export async function reactivateAvailabilityAlertSubscriberForUser(args: {
  user: AppUser | null;
  subscriberId: string;
}): Promise<AvailabilityAlertSubscriberRow> {
  const actor = requireAdminOrManager(args.user);

  try {
    return await reactivateAvailabilityAlertSubscriber({
      subscriberId: args.subscriberId,
      actorId: actor.id,
    });
  } catch (error) {
    handleAvailabilityAlertError(error);
  }
}

export async function listUnitAvailabilityAlertEventsForUser(args: {
  user: AppUser | null;
  limit?: number;
  signal?: AbortSignal;
}): Promise<UnitAvailabilityAlertEventRow[]> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  try {
    return await listUnitAvailabilityAlertEvents({ limit: args.limit, signal: args.signal });
  } catch (error) {
    handleAvailabilityAlertError(error);
  }
}

export async function countPendingAvailabilityAlertEmailsForUser(args: {
  user: AppUser | null;
  signal?: AbortSignal;
}): Promise<number> {
  const actor = requireAdminOrManager(args.user);
  void actor;

  try {
    return await countAvailabilityAlertOutbox({ status: 'pending', signal: args.signal });
  } catch (error) {
    handleAvailabilityAlertError(error);
  }
}

function handleAvailabilityAlertError(error: unknown): never {
  if (isServiceError(error)) {
    throw error;
  }

  throw normalizeError(error);
}
